import * as React from 'react';

export interface IAccordionOnClickParams {
  title: string;
  isActive: boolean;
  index: number;
}

interface IUseAccordionsBehaviourResult {
  accordionsCollectionRef: React.MutableRefObject<(HTMLDivElement | null)[]>;
  onAccordionClick: (params: IAccordionOnClickParams) => void;
  activeAccordeonIndex: number | null;
}

interface IUseAccordionsBehaviourParams {
  isModalOpen: boolean;
  activeAccordeonIndex: number | null;
  scrollDelay?: number;
}

const DEFAULT_SCROLL_DELAY = 250;

/**
 * Хук, управляющий поведением аккордеонов:
 *  1. Автоскролл к заголовку аккордеона, если при его открытии он скрылся за экран.
 *  2. Одновременно может быть открыто не более 1 аккордеона.
 */
export const useAccordionsBehaviour = (params: IUseAccordionsBehaviourParams): IUseAccordionsBehaviourResult => {
  const { isModalOpen, activeAccordeonIndex, scrollDelay = DEFAULT_SCROLL_DELAY } = params;

  const accordionsCollectionRef = React.useRef<Array<HTMLDivElement | null>>([]);
  const [activeItem, setActiveItem] = React.useState<number | null>(activeAccordeonIndex);

  React.useEffect(() => {
    if (!isModalOpen) {
      setActiveItem(null);
    }
  }, [isModalOpen]);

  React.useEffect(() => {
    let timer: NodeJS.Timeout;

    if (accordionsCollectionRef.current.length !== 0 && activeItem !== null) {
      const activeElement = accordionsCollectionRef.current[activeItem] as HTMLDivElement;
      const topPotision = activeElement.getBoundingClientRect().top;
      const isTopHidden = topPotision < 0;

      if (isTopHidden) {
        timer = setTimeout(() => {
          activeElement.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          });
        }, scrollDelay);
      }
    }

    return () => clearTimeout(timer);
  }, [activeItem, scrollDelay]);

  const onAccordionClick = (params: IAccordionOnClickParams) => {
    const { index, isActive } = params;

    setActiveItem(isActive ? null : index);
  };

  return { accordionsCollectionRef, onAccordionClick, activeAccordeonIndex: activeItem };
};
